/* live_v2/charts.js — LiveSlates ECharts layer (Xav 2026-06-14).
 *
 * THE LAB's graphs were hand-rolled SVG (low ceiling). This is the shared
 * ECharts foundation every Lab widget builds on: one create/dispose path,
 * one theme, so the charts are consistent and premium.
 *
 * Lifecycle note: Alpine doesn't call a destroy() hook on widget x-data, and
 * ECharts keeps an internal DOM→instance registry that pins instances even
 * after their element is removed (section switch / panel delete). So we
 * register every chart and SWEEP detached ones on each create — bounding the
 * live instance count to what's actually mounted. ResizeObserver keeps each
 * chart sized to its (resizable) panel.
 *
 * Usage in a widget:
 *   init() {
 *     this._chart = LSChart.create(this.$refs.chart);
 *     this._draw();
 *     this.$watch('lastUpdated', () => this._draw());
 *   }
 *   _draw() { this._chart && this._chart.setOption(this._option(), true); }
 */
window.LSChart = (function () {
  const registry = new Set();

  // Lazy ECharts loader (Xav 2026-06-14) — the 1MB library is render-
  // blocking when eager, slowing EVERY dashboard load. Load it on demand
  // instead, so it only hits the wire when a chart widget mounts (i.e. the
  // user opens THE LAB). First load fetches; later loads are cache hits.
  const ECHARTS_SRC = '/static/js/vendor/echarts.min.js?v=5.5.1';
  let _loadPromise = null;
  function _load() {
    if (window.echarts) return Promise.resolve();
    if (_loadPromise) return _loadPromise;
    _loadPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = ECHARTS_SRC; s.async = true;
      s.onload  = () => resolve();
      s.onerror = () => { _loadPromise = null; reject(new Error('echarts load failed')); };
      document.head.appendChild(s);
    });
    return _loadPromise;
  }
  /** Run cb once ECharts is available, lazy-loading it on first call. */
  function ready(cb) { _load().then(() => { if (cb) cb(); }).catch(() => {}); }

  /** dataZoom for scrolling a vertical (yAxis) category list WITHIN a fixed
   *  panel — shows the top `windowSize` and wheel/drag pans through the rest.
   *  Keeps THE LAB no-scroll while still surfacing all 50/80 rows. The span
   *  is locked (wheel pans, doesn't zoom). (Xav 2026-06-14) */
  function vScroll(windowSize) {
    return [{
      type: 'inside', yAxisIndex: 0,
      zoomLock: true, zoomOnMouseWheel: false,
      moveOnMouseWheel: true, moveOnMouseMove: true,
      startValue: 0, endValue: Math.max(0, (windowSize | 0) - 1),
    }];
  }

  /** Dispose any registered chart whose element has left the document. */
  function sweep() {
    for (const c of [...registry]) {
      let el = null;
      try { el = c.getDom(); } catch (_) { /* disposed */ }
      if (!el || !document.contains(el)) {
        try { if (c.__ro) c.__ro.disconnect(); } catch (_) {}
        try { c.dispose(); } catch (_) {}
        registry.delete(c);
      }
    }
  }

  /** Init (or reuse) an ECharts instance on `el`, wired for auto-resize. */
  function create(el) {
    if (!el || !window.echarts) return null;
    sweep();
    const existing = echarts.getInstanceByDom(el);
    if (existing) return existing;
    // SVG, NOT CANVAS — and the reason is pointer accuracy, not looks.
    //
    // zrender maps a pointer event to chart coordinates with, for a canvas:
    //     zrX = clientX - el.getBoundingClientRect().left
    // and nothing scales between the canvas's internal coordinate space and
    // its rendered size. Those two differ by exactly the CSS `zoom` factor,
    // and Interface scale (Settings > Display) sets zoom on documentElement
    // for every non-touch device. At 90% the hit areas sat ~1.5cm to the RIGHT
    // of their dots on the value scatter, the error growing left-to-right
    // across the chart (Xav, 2026-10-14). Every chart had it; the scatter is
    // just where you aim at one specific mark rather than read a bar.
    //
    // zoom cannot be detected from a transform matrix - it is not a transform,
    // so it never appears in getComputedStyle().transform. But zrender's
    // NON-canvas path (transformCoordWithViewport) does not try: it injects
    // four zero-size marker divs at the element's corners, reads each one's
    // getBoundingClientRect (rendered space) AND offsetLeft/offsetTop (layout
    // space), and solves the transform between them. It MEASURES the mapping,
    // so it picks zoom up for free. Canvas elements are explicitly excluded
    // from that path (`!isCanvasEl`), which is why they alone are wrong.
    //
    // Nothing depended on the canvas backend - no getDataURL, no toDataURL, no
    // code reaching for a <canvas> child. At a few hundred marks SVG is well
    // inside its comfort zone; revisit only if a chart ever draws thousands.
    const c = echarts.init(el, null, { renderer: 'svg' });
    // a chart is an image to a screen reader, named by the takeaway above it
    // (the panel's first sentence) or its title - a bare canvas said nothing
    // (audit v2 run 3, FIT-16)
    try {
      const cell = el.closest('.ls-cell, [data-widget-id]');
      const take = cell && cell.querySelector('.ls-lab-take, .ls-cell-title-pill');
      el.setAttribute('role', 'img');
      if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', (take && take.textContent.trim()) || 'Chart');
    } catch (_) { /* a label is a courtesy, never a failure */ }
    // Every chart speaks the app's typeface. Without this ECharts fell back to
    // its own sans-serif, the one face on the board outside the type system
    // (Broadcast audit run 1, TYPE-8). Applied on every setOption, because
    // widgets replace their whole option (notMerge) on redraw.
    const font = cssVar('--font', '') || 'sans-serif';
    const set = c.setOption.bind(c);
    c.setOption = (opt, ...rest) => {
      if (opt && typeof opt === 'object') {
        opt = Object.assign({}, opt, { textStyle: Object.assign({ fontFamily: font }, opt.textStyle || {}) });
      }
      return set(opt, ...rest);
    };
    const ro = new ResizeObserver(() => { try { c.resize(); } catch (_) {} });
    ro.observe(el);
    c.__ro = ro;
    registry.add(c);
    return c;
  }

  /** True if the app is in dark mode (affects axis/text colours). */
  function isDark() {
    return document.documentElement.classList.contains('dark');
  }

  /** Read a CSS custom property off :root (falls back if unset). Lets chart JS
   *  — which can't use var() inside a canvas/echarts colour string — still
   *  source its brand colours from the same token layer the CSS uses, so a
   *  re-theme flows through the charts too instead of drifting on copied hex. */
  function cssVar(name, fallback) {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  }

  /** [base, bright] emerald pair for monochrome rank ramps (leader = bright).
   *  Single source for the `const emerald = isDark ? [...] : [...]` that was
   *  copy-pasted across five widget charts. Reads --ls-accent* so it stays in
   *  lock-step with the CSS accent tokens. */
  function accentPair(dark) {
    const a   = cssVar('--ls-accent',       '#10b981'); // emerald-500
    const hi  = cssVar('--ls-accent-hi',    '#34d399'); // emerald-400 (dark bright)
    const hov = cssVar('--ls-accent-hover', '#059669'); // emerald-600 (light base)
    return dark ? [a, hi] : [hov, a];
  }

  /** Theme tokens — resolved per-call so dark mode is honoured. The charts
   *  sit inside white (or slate-900) panel cards, so axes/text are tuned to
   *  read on the card surface; tooltips are always dark for contrast. */
  function theme() {
    const dark = isDark();
    const bc = document.documentElement.dataset.lsSkin === 'broadcast';
    return {
      // Per-series categorical palette (Xav 2026-06-14: charts need colour,
      // not all-emerald). Matches live.js graphLineupColor so a lineup keeps
      // the SAME colour across the Race, Lineup Scores, etc.
      palette: ['#10b981', '#6366f1', '#f59e0b', '#ef4444', '#0ea5e9',
                '#a855f7', '#ec4899', '#14b8a6', '#84cc16', '#f97316',
                '#3b82f6', '#eab308'],
      // Cool→hot scale for heatmaps / single-metric intensity.
      heat: ['rgba(59,130,246,0.10)', '#60a5fa', '#22d3ee', '#34d399',
             '#fbbf24', '#fb923c', '#ef4444'],
      // Broadcast: the board's own accent, and axis / label greys that pass
      // AA on its panels - the site's read 2.6:1 / 3.8:1 there (v2 run 2,
      // COLOUR-3 / COLOUR-5). Other boards unchanged.
      emerald:    (bc && cssVar('--bc-accent', '')) || cssVar('--ls-accent', '#10b981'),
      emeraldDim: dark ? cssVar('--ls-accent-hi', '#34d399') : cssVar('--ls-accent-hover', '#059669'),
      blue:       dark ? '#93c5fd' : '#3b82f6',
      axisLabel:  bc ? (cssVar('--bc-dim', '') || (dark ? '#7d8f99' : '#5b6b74')) : (dark ? '#64748b' : '#94a3b8'),
      splitLine:  dark ? 'rgba(148,163,184,0.10)' : 'rgba(15,23,42,0.06)',
      axisLine:   dark ? 'rgba(148,163,184,0.20)' : 'rgba(15,23,42,0.10)',
      label:      dark ? '#cbd5e1' : '#334155',
      labelDim:   bc ? (cssVar('--bc-dim', '') || (dark ? '#7d8f99' : '#5b6b74')) : (dark ? '#64748b' : '#94a3b8'),
      tooltip: {
        backgroundColor: 'rgba(7,11,20,0.94)',
        borderColor:     'rgba(16,185,129,0.4)',
        borderWidth:     1,
        textStyle:       { color: '#e2e8f0', fontSize: 12 },
        extraCssText:    'box-shadow:0 8px 24px -8px rgba(0,0,0,0.5);border-radius:8px;',
      },
      // A top→bottom emerald area gradient (lines/areas) for a single hue.
      areaGrad(hex) {
        return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: hex + '55' },
          { offset: 1, color: hex + '00' },
        ]);
      },
      // A left→right bar gradient for horizontal bars.
      barGrad(hex) {
        return new echarts.graphic.LinearGradient(0, 0, 1, 0, [
          { offset: 0, color: hex + 'cc' },
          { offset: 1, color: hex },
        ]);
      },
      font: 'inherit',
    };
  }

  /** Shared Alpine mixin for the ECharts panels. Every panel repeated the same
   *  chart lifecycle (mount on ready, redraw on `lastUpdated`) + the same
   *  emerald-fade rank colour. Spread it into a widget factory so the widget
   *  only supplies `_option()`:
   *      factory: () => ({ ...LSChart.panelBase(), _option() { … } })
   *  No getters here → spread-safe (the host's format getters _fmtScore /
   *  _scoreUnitSfx are read directly in each _option, so they stay reactive). */
  function panelBase() {
    return {
      _chart: null,
      init() {
        ready(() => this.$nextTick(() => {
          this._chart = create(this.$refs.lsChart);
          this._draw();
        }));
        this.$watch('lastUpdated', () => this._draw());
      },
      _draw() {
        if (!this._chart) return;
        // In-place merge (not notMerge/replaceMerge) — a full teardown each tick
        // flickers; merging animates to the new values and settles in one tick.
        this._chart.setOption(this._option());
      },
      /** Emerald→slate rank-fade colour (leader brightest). Returns a closure so
       *  accentPair/isDark are read ONCE per _option, not per data point. */
      _rankColorFn(dark) {
        const emerald = accentPair(dark);
        const fade = dark ? '#475569' : '#cbd5e1';
        return (i, n) => {
          if (i === 0) return emerald[1];
          const tFrac = Math.min(1, i / Math.max(1, n - 1));
          return tFrac < 0.5 ? emerald[0] : fade;
        };
      },
    };
  }

  return { create, sweep, theme, isDark, cssVar, accentPair, ready, vScroll, panelBase };
})();
